<template>
    <DsModal
        :is-open="isOpen"
        show-close
        :title="$t('email.broadcast.validated.domains.modal.title')"
        @close="$emit('close')"
        @back="$emit('close')"
    >
        <template #default>
            <DsInlineAlert
                v-if="submitted && !isValid"
                type="critical"
                leading-icon
                class="custom-sender-alert-container"
            >
                <span class="custom-sender-alert-text">
                    {{ $t('unlayerEmail.unifiedFlow.customSender.errorText') }}
                </span>
            </DsInlineAlert>

            <DsInlineAlert
                v-else-if="isMergeField"
                type="warning"
                leading-icon
                class="custom-sender-alert-container"
            >
                <span class="custom-sender-alert-text">
                    {{ $t('unlayerEmail.unifiedFlow.customSender.mergeFieldWarning') }}
                </span>
            </DsInlineAlert>

            <DsInlineAlert
                v-else-if="emailLabel"
                type="info"
                leading-icon
                class="custom-sender-alert-container"
            >
                <span class="preview-email-label-container custom-sender-alert-text">
                    {{ $t('unlayerEmail.unifiedFlow.customSender.emailPreview.pre') }}
                    <span class="preview-email-label">{{ emailLabel }}</span>
                    {{ $t('unlayerEmail.unifiedFlow.customSender.emailPreview.post') }}
                </span>
            </DsInlineAlert>

            <DsInputField
                v-model="displayName"
                required
                trim
                class="display-name-input"
                data-qa="name-input"
                :label="$t('unlayerEmail.unifiedFlow.customSender.displayNameLabel')"
                :invalid="!isDisplayNameValid"
                :submitted="submitted"
            >
                <template #error>
                    <span v-if="!isDisplayNameValid" data-qa="display-name-error">
                        {{ $t('unlayerEmail.unifiedFlow.customSender.displayNameError') }}
                    </span>
                </template>
            </DsInputField>

            <div
                v-click-outside="{ events: ['mousedown'], handler: () => isDropdownOpen = false }"
                tabindex="-1"
                class="email-address-container"
                data-qa="input-container"
            >
                <DsInputField
                    ref="emailAddressInputField"
                    v-model="emailAddress"
                    required
                    trim
                    :label="$t('unlayerEmail.unifiedFlow.customSender.emailAddrLabel')"
                    :invalid="!isEmailFieldValid"
                    :submitted="submitted"
                    name="custom-sender"
                    class="email-address-input-field"
                    data-qa="email-input"
                    @focus="openDropdown"
                >
                    <template #trailing>
                        <div
                            class="custom-sender-dropdown-icon"
                            data-qa="dropdown-toggle"
                            @click="isDropdownOpen = !isDropdownOpen"
                        >
                            <DsIcon name="chevron-down" />
                        </div>
                    </template>

                    <template #error>
                        <span v-if="!isEmail && !isMergeField"> {{ $t('unlayerEmail.unifiedFlow.customSender.emailAddressError') }} </span>
                        <span v-else-if="isEmail && !isEmailVerified"> {{ $t('unlayerEmail.unifiedFlow.customSender.unverifiedEmailDomain') }} </span>
                    </template>
                </DsInputField>

                <DsDropdown
                    class="custom-sender-dropdown"
                    :is-open="isDropdownOpen"
                    @focus.capture="openDropdown"
                >
                    <template #menu>
                        <DsMultiselectMenu
                            :key="dropdownResetTrigger"
                            :options="options"
                            categorized
                            @input="onDropdownItemSelection"
                        />
                    </template>
                </DsDropdown>
            </div>

            <div class="custom-sender-help-container">
                <div>
                    <span> {{ $t('unlayerEmail.unifiedFlow.customSender.domainNotFound') }} </span>
                    <DsLink external :href="setUpLink">
                        {{ $t('unlayerEmail.unifiedFlow.customSender.connectDomain') }}
                    </DsLink>
                </div>

                <div>
                    <span> {{ $t('unlayerEmail.unifiedFlow.customSender.learnMore') }} </span>
                    <DsLink external :href="$options.LEARN_MORE_LINK">
                        {{ $t('global.learnMore') }}
                    </DsLink>
                </div>
            </div>

            <DsFilledButton
                class="custom-sender-save-btn"
                data-qa="save-btn"
                @click="save"
            >
                {{ $t('global.save') }}
            </DsFilledButton>
        </template>
    </DsModal>
</template>

<script>
import {
    DEFAULT_FROM_VALUE,
    DKIM_LEARN_MORE_LINK_ALL_EDITIONS as LEARN_MORE_LINK,
    DKIM_SET_UP_LINK_NON_SPA,
} from '@/components/broadcast/constants/emailBroadcast.constants';
import { isValidEmailAddress, MERGE_FIELD_REGEX } from '@/utils/email.util';
import {
    getFromAddress,
    getEmailName,
} from '@/components/broadcast/utils/emailBroadcast.util';
import { isSpaApp } from '@/api/shared';

export default {
    LEARN_MORE_LINK,

    props: {
        isOpen: Boolean,
        domains: Array,
        address: String,
        environment: {
            type: String,
            default: 'production',
        },
        mergeFields: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            displayName: '',
            emailAddress: '',
            dropdownResetTrigger: 0,
            isDropdownOpen: false,
            submitted: false,
            isSpaApp: false,
        };
    },

    async created() {
        this.isSpaApp = await isSpaApp();
    },

    watch: {
        displayName() {
            this.submitted = false;
        },

        emailAddress(val) {
            this.submitted = false;
            this.isDropdownOpen = val.length === 0;
        },

        address: {
            handler(val) {
                const email = getFromAddress(val || '');
                const isCustomAddress = !val ? false
                    : DEFAULT_FROM_VALUE !== val
                    && (this.isDomainValid(email) || MERGE_FIELD_REGEX.test(email));

                if (isCustomAddress) {
                    this.emailAddress = email;
                    this.displayName = getEmailName(this.address);
                } else {
                    this.emailAddress = '';
                    this.displayName = '';
                }
            },
            immediate: true,
        },
    },

    computed: {
        options() {
            const customFields = this.mergeFields.find(({ value }) => value === 'customFields')?.fields || [];
            const emailFields = customFields.filter((cf) => cf.type === 'Email');

            return [
                {
                    category: { label: this.$t('unlayerEmail.unifiedFlow.customSender.validatedDomains') },
                    options: this.domains,
                },
                {
                    category: { label: this.$t('unlayerEmail.unifiedFlow.customSender.customFields') },
                    options: emailFields,
                }
            ];
        },

        isDisplayNameValid() {
            return (this.displayName || '').length > 0;
        },

        isMergeField() {
            return MERGE_FIELD_REGEX.test(this.emailAddress);
        },

        isEmail() {
            return isValidEmailAddress(this.emailAddress);
        },

        isEmailVerified() {
            return this.isDomainValid(this.emailAddress);
        },

        isEmailFieldValid() {
            if (this.isMergeField) return true;

            if (this.isEmail && this.isEmailVerified) return true;

            return false;
        },

        isValid() {
            return this.isEmailFieldValid && this.isDisplayNameValid;
        },

        emailLabel() {
            if (!this.emailAddress) return '';

            const displayName = this.displayName || this.$t('unlayerEmail.unifiedFlow.customSender.blank');

            return `"${displayName}<${this.emailAddress}>"`;
        },

        setUpLink() {
            if (this.isSpaApp) {
                const path = '/settings/domains';

                switch (this.environment.toLowerCase()) {
                case 'production':
                default:
                    return `https://keap.app${path}`;
                case 'development':
                case 'sandbox':
                    return `http://localhost:10239${path}`;
                case 'integration':
                    return `https://integration.keap.app${path}`;
                case 'proofing':
                case 'staging':
                    return `https://staging.keap.app${path}`;
                }
            }

            return DKIM_SET_UP_LINK_NON_SPA;
        },
    },

    methods: {
        openDropdown() {
            this.isDropdownOpen = (this.emailAddress || '').length === 0;
        },

        isDomainValid(value) {
            try {
                const domain = window.URL.parse(`mailto://${value}`).host;

                return this.domains.findIndex(({ value: v }) => domain === v.slice(1)) >= 0;
            } catch (e) {
                return false;
            }
        },

        save() {
            this.submitted = true;

            if (!this.isValid) return;

            this.$emit('custom-sender-selected', {
                displayName: this.displayName,
                emailAddress: this.emailAddress,
            });
            this.$emit('close');
        },

        async selectDomain(value) {
            if (!this.emailAddress || this.isMergeField) this.emailAddress = 'example';

            const selectionLength = this.emailAddress.length;

            this.emailAddress += value;

            const el = this.$refs.emailAddressInputField.$el.querySelector('input');

            await this.$nextTick();

            el.focus();
            el.setSelectionRange(0, selectionLength);
        },

        async onDropdownItemSelection({ value }) {
            if (MERGE_FIELD_REGEX.test(value)) {
                this.emailAddress = value;
            } else {
                await this.selectDomain(value);
            }

            this.isDropdownOpen = false;
            this.dropdownResetTrigger++;
        },
    }
}
</script>

<style scoped lang="scss">
.display-name-input {
    margin-bottom: $spacing-200;
}

.custom-sender-alert-container,
.email-address-container {
    margin-bottom: $spacing-300;
}

.custom-sender-alert-text {
    font-size: $font-size-sm;
}

.email-address-container {
    position: realtive;
}

.display-name-input,
.email-address-input-field {
    --input-padding-bottom: 0;
    --input-padding-top: 0;
}

.email-address-input-field {
    --input-margin-bottom: 0;
}

.preview-email-label-container {
    display: inline;
}

.custom-sender-dropdown {
    --dropdown-minwidth: 100%;

    :deep ul {
        margin: 0;
    }
}

.custom-sender-dropdown-icon {
    cursor: pointer;
}

.preview-email-label {
    font-family: 'monospace';
    line-height: $line-height-md;
    font-weight: $font-weight-regular;
    word-break: break-word;
}

.custom-sender-help-container {
    color: $color-text-subtle;

    > :first-child {
        margin-bottom: $spacing-050;
    }
}

.custom-sender-save-btn {
    width: min-content;
    margin-top: $spacing-200;
}
</style>
